import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { clsx } from "@/utils";
import { DateTimePanel } from "./DateTimePanel";
import { OtherDetailsPanel } from "./OtherDetailsPanel";
import { PassengerPanel } from "./PassengerPanel";
import type { TripDetailItem } from "./TripDetailsPanel";
import { VehicleTransferPanel } from "./VehicleTransferPanel";

/**
 * The fields for the selected Trip Details row. Presented as a side-by-side card on md+
 * (TripDetailSubPanel) and as a bottom-sheet drawer on mobile (TripDetailSubPanelSheet).
 */

/**
 * md+ presentation: a floating card sitting beside the Trip Details list. `onAdvance` is called by a
 * sub-panel once its save/proceed succeeds so the list opens the next sub-step automatically (the
 * mobile sheet closes back to the list instead — see TripDetailSubPanelSheet).
 *
 * `key={item}` remounts the card on every selection so the slide-in below replays — the panel slides
 * in from the list's side each time a row is clicked, so the details read as belonging to that row.
 */
export const TripDetailSubPanel = ({
  item,
  onAdvance,
  onUpdateFlight,
  forceValidate = false,
  closing = false,
  className,
}: {
  item: TripDetailItem;
  onAdvance: () => void;
  /** The Date/Time panel's "Update flight" link on a disrupted flight — back to the Address step. */
  onUpdateFlight?: () => void;
  /** Ask the open panel to show its inline field errors immediately (set after a failed "Next Step"). */
  forceValidate?: boolean;
  /** Edit mode: Confirm plays the reverse (slide-out) keyframe before the parent unmounts the card. */
  closing?: boolean;
  className?: string;
}) => (
  // No padding here — panels pad their own body (PassengerPanel needs an unpadded edge for its full-bleed divider).
  // The slide-in runs as a compositor-driven CSS keyframe (transform-gpu + will-change) so it stays smooth
  // even while a heavy sub-panel (calendar / vehicle list) mounts on the same frame. Replays each card
  // switch via the parent's key={item}; on Confirm-close it swaps to the slide-out keyframe.
  <div
    className={clsx(
      "flex transform-gpu flex-col overflow-hidden rounded-2xl border border-neutral-mid-gray bg-white shadow-lg will-change-transform",
      closing ? "animate-slide-out-panel" : "animate-slide-in-panel",
      className
    )}
  >
    {item === "passenger" ? (
        <PassengerPanel layout="card" onClose={() => undefined} onAdvance={onAdvance} forceValidate={forceValidate} />
      ) : item === "datetime" ? (
        <DateTimePanel
          layout="card"
          onClose={() => undefined}
          onAdvance={onAdvance}
          onUpdateFlight={onUpdateFlight}
          forceValidate={forceValidate}
        />
      ) : item === "vehicle" ? (
        <VehicleTransferPanel layout="card" onClose={() => undefined} onAdvance={onAdvance} forceValidate={forceValidate} />
      ) : (
        // On the card, Proceed opens the next sub-step (mirrors the other panels' onAdvance).
        <OtherDetailsPanel layout="card" onProceed={onAdvance} onClose={() => undefined} forceValidate={forceValidate} />
      )}
    </div>
  );

interface TripDetailSubPanelSheetProps {
  item: TripDetailItem;
  open: boolean;
  /** Ask the open panel to show its inline field errors immediately (set after a failed "Next Step"). */
  forceValidate?: boolean;
  onClose: () => void;
  onProceed: () => void;
  /** The Date/Time panel's "Update flight" link on a disrupted flight — back to the Address step. */
  onUpdateFlight?: () => void;
  className?: string;
}

/**
 * Mobile presentation: a bottom-sheet drawer over a dimmed backdrop, with a Proceed action and a
 * Close link. Rendered inside the map container (not portalled) so `md:hidden` keeps it mobile-only.
 */
export const TripDetailSubPanelSheet = ({
  item,
  open,
  forceValidate = false,
  onClose,
  onProceed,
  onUpdateFlight,
  className,
}: TripDetailSubPanelSheetProps) => {
  // Slide up from the bottom on open. Done via a mount-then-transition toggle rather than
  // `animate-in slide-in-from-bottom` — the project's tailwind config overrides the `enter`
  // keyframe (a scale "pop"), which swallows tailwindcss-animate's translate modifiers.
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    if (!open) {
      setEntered(false);
      return;
    }
    const id = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(id);
  }, [open]);

  if (!open || typeof document === "undefined") return null;

  // Portalled to <body> as a true modal (fixed inset-0) so the dimmed backdrop covers the whole
  // viewport — header included — like the app's other modals. `md:hidden` keeps it mobile-only.
  return createPortal(
    <div className={clsx("fixed inset-0 z-50", className)}>
      <div
        className={clsx("absolute inset-0 bg-neutral-black/50 transition-opacity duration-300", entered ? "opacity-100" : "opacity-0")}
        onClick={onClose}
        aria-hidden
      />
      <div
        className={clsx(
          "absolute inset-x-0 bottom-0 flex max-h-[85%] flex-col rounded-t-2xl bg-white transition-transform duration-300 ease-out",
          entered ? "translate-y-0" : "translate-y-full"
        )}
      >
        {item === "passenger" ? (
          // The passenger panel pads its own body + supplies its contextual footer (Proceed / Proceed with Guest + Close).
          <PassengerPanel layout="sheet" onClose={onClose} forceValidate={forceValidate} />
        ) : item === "datetime" ? (
          <DateTimePanel layout="sheet" onClose={onClose} onUpdateFlight={onUpdateFlight} forceValidate={forceValidate} />
        ) : item === "vehicle" ? (
          <VehicleTransferPanel layout="sheet" onClose={onClose} forceValidate={forceValidate} />
        ) : (
          // On the sheet, Proceed closes back to the list (mirrors the other panels).
          <OtherDetailsPanel layout="sheet" onProceed={onProceed} onClose={onClose} forceValidate={forceValidate} />
        )}
      </div>
    </div>,
    document.body
  );
};
